import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ModuleMaterialForm from '@/components/ModuleMaterialForm';
import Seo from '@/components/Seo';
import { getCourse, getInstructor } from '@/data/catalog';
import { useAuth } from '@/hooks/useAuth';
import {
  createEnrollmentRequest,
  fetchMyEnrollment,
  statusLabel,
  type CourseEnrollment } from
'@/lib/enrollments';
import { fetchCourseMaterials, materialForModule, type ModuleMaterial } from '@/lib/materials';

function CourseDetail() {
  const { slug = '' } = useParams();
  const course = getCourse(slug);
  const instructor = course ? getInstructor(course.instructorId) : undefined;
  const { isAuthenticated, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(user?.userName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [enrollment, setEnrollment] = useState<CourseEnrollment | undefined>();
  const [materials, setMaterials] = useState<ModuleMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (user?.userName) setFullName((current) => current || user.userName);
    if (user?.email) setEmail((current) => current || user.email);
  }, [user?.userName, user?.email]);

  useEffect(() => {
    const userId = user?.userId;
    const courseId = course?.id;
    if (!userId || !courseId) {
      setEnrollment(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMyEnrollment(userId, courseId).
    then((record) => {
      if (!cancelled) setEnrollment(record);
    }).
    catch((err) => {
      console.error('No se pudo consultar la inscripción:', err);
      if (!cancelled) setFormError('No se pudo consultar tu inscripción.');
    }).
    finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.userId, course?.id]);

  useEffect(() => {
    const courseId = course?.id;
    if (!courseId) return;
    let cancelled = false;
    fetchCourseMaterials(courseId).
    then((list) => {
      if (!cancelled) setMaterials(list);
    }).
    catch((err) => {
      console.error('No se pudieron cargar los materiales:', err);
    });
    return () => {
      cancelled = true;
    };
  }, [course?.id]);

  if (!course || !instructor) {
    return (
      <section className="mx-auto max-w-3xl px-5 py-20">
        <h1 className="font-['Fraunces'] text-3xl">Curso no encontrado</h1>
        <Link to="/cursos" className="mt-4 inline-block text-sm underline">
          Volver al catálogo
        </Link>
      </section>);

  }

  const onEnroll = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    if (!isAuthenticated || !user?.userId) {
      navigate(`/acceso?next=/cursos/${course.slug}`);
      return;
    }
    if (!fullName.trim() || !email.trim()) {
      setFormError('Nombre y correo son obligatorios.');
      return;
    }
    setSubmitting(true);
    try {
      const record = await createEnrollmentRequest({
        userId: user.userId,
        courseId: course.id,
        courseSlug: course.slug,
        courseTitle: course.title,
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        note: note.trim()
      });
      setEnrollment(record);
    } catch (err) {
      console.error('No se pudo enviar la solicitud:', err);
      setFormError('No se pudo enviar la solicitud. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const canEnterClassroom = enrollment?.status === 'approved' && enrollment?.documentsAccess;

  return (
    <>
      <Seo title={course.title} description={course.excerpt} />
      <article>
        <header className="mx-auto grid max-w-6xl gap-8 px-5 py-10 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#C41E3A]">{course.category}</p>
            <h1 className="mt-3 font-['Fraunces'] text-4xl sm:text-5xl">{course.title}</h1>
            <p className="mt-4 max-w-xl text-lg text-[#1E3A5F]/70">{course.subtitle}</p>
            <dl className="mt-8 grid max-w-lg grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-[#1E3A5F]/45">Nivel</dt>
                <dd>{course.level}</dd>
              </div>
              <div>
                <dt className="text-[#1E3A5F]/45">Duración</dt>
                <dd>{course.duration}</dd>
              </div>
              <div>
                <dt className="text-[#1E3A5F]/45">Sesiones</dt>
                <dd>{course.sessions}</dd>
              </div>
            </dl>
          </div>
          <img src={course.image} alt="" className="h-72 w-full object-cover"></img>
        </header>

        <div className="mx-auto grid max-w-6xl gap-12 px-5 pb-16 lg:grid-cols-[1fr_360px] lg:px-8">
          <div>
            <h2 className="font-['Fraunces'] text-3xl text-[#0C4A6E]">Sobre el curso</h2>
            <p className="mt-4 max-w-2xl leading-7 text-[#1E3A5F]/75 text-base">{course.description}</p>
            <h3 className="mt-10 font-['Fraunces'] text-xl">Al terminar serás capaz de</h3>
            <ul className="mt-4 space-y-2 text-[#1E3A5F]/75 text-lg text-[#7F1D1D]">
              {course.outcomes.map((item) =>
              <li key={item} className="border-l border-[#C41E3A] pl-3">
                  {item}
                </li>
              )}
            </ul>
            <h3 className="mt-10 font-['Fraunces'] text-xl text-[#1A2E05]">Módulos y presentaciones</h3>
            <ol className="mt-4 space-y-4">
              {course.modules.map((module, index) => {
                const material = materialForModule(materials, module.id);
                return (
                  <li key={module.id} className="border border-[#1E3A5F]/10 p-4">
                  <p className="text-xs text-[#C41E3A]">Sesión {index + 1} · {module.duration}</p>
                  <p className="mt-1 font-['Fraunces'] text-lg">{module.title}</p>
                  <p className="mt-2 text-sm text-[#1E3A5F]/65">{module.summary}</p>
                  <p className="mt-2 text-xs text-[#1E3A5F]/45">{module.slides.length} diapositivas en el aula</p>
                  {isAdmin && user?.userId ?
                    <ModuleMaterialForm
                      courseId={course.id}
                      courseSlug={course.slug}
                      moduleId={module.id}
                      creator={user.userId}
                      material={material}
                      onSaved={(record) => {
                        setMaterials((current) => {
                          const without = current.filter((item) => item.moduleId !== record.moduleId);
                          return [record, ...without];
                        });
                      }} /> :
                    null}
                </li>);

              })}
            </ol>
            <div className="mt-10 flex items-center gap-4">
              <img src={instructor.photo} alt="" className="h-16 w-16 object-cover" />
              <div>
                <p className="font-['Fraunces']">{instructor.name}</p>
                <p className="text-xs text-[#1E3A5F]/55">{instructor.role} · {instructor.city}</p>
              </div>
            </div>
          </div>

          <aside className="h-fit border border-[#1E3A5F]/15 bg-[#EDE6D9] p-6">
            <h2 className="font-['Fraunces'] text-2xl">Inscripción</h2>
            {loading ?
            <p className="mt-4 text-sm text-[#1E3A5F]/65">Consultando tu solicitud…</p> :
            enrollment ?
            <div className="mt-4 text-sm leading-relaxed">
                <p className="text-xs uppercase tracking-wide text-[#C41E3A]">{statusLabel(enrollment.status)}</p>
                {enrollment.status === 'pending' ?
              <p className="mt-3">
                    Solicitud enviada. Un administrador debe aceptarte en el curso o darte acceso a los documentos.
                  </p> :
              null}
                {enrollment.status === 'approved' && !enrollment.documentsAccess ?
              <p className="mt-3">
                    Ya estás aceptado en el curso. El aula de documentos se abrirá cuando un administrador te dé acceso.
                  </p> :
              null}
                {enrollment.status === 'rejected' ?
              <p className="mt-3">Esta solicitud no fue aceptada. Si crees que es un error, escribe a soporte.</p> :
              null}
                {canEnterClassroom ?
              <>
                    <p className="mt-3">Plaza aceptada y documentos desbloqueados. El aula ya está abierta para ti.</p>
                    <Link
                  to={`/aula/${course.slug}`}
                  className="mt-5 inline-block bg-[#1E3A5F] px-4 py-2 text-[#F4EFE6]">

                      Entrar al aula
                    </Link>
                  </> :
              null}
              </div> :

            <form onSubmit={onEnroll} className="mt-4 space-y-3">
                <p className="text-sm text-[#1E3A5F]/65">
                  {isAuthenticated ?
                'Envía tu solicitud. Un administrador revisará el acceso al curso y a los documentos.' :
                'Inicia sesión o regístrate para solicitar la inscripción.'}
                </p>
                <label className="block text-xs">
                  Nombre completo
                  <input
                  required
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm" />

                </label>
                <label className="block text-xs">
                  Correo
                  <input
                  required
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm" />

                </label>
                <label className="block text-xs">
                  Teléfono (opcional)
                  <input
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm" />

                </label>
                <label className="block text-xs">
                  Nota para el instructor
                  <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm" />

                </label>
                {formError ? <p className="text-xs text-[#C41E3A]">{formError}</p> : null}
                <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1E3A5F] py-2.5 text-sm text-[#F4EFE6] disabled:opacity-50">

                  {isAuthenticated ? submitting ? 'Enviando…' : 'Solicitar inscripción' : 'Acceder para inscribirme'}
                </button>
              </form>
            }
          </aside>
        </div>
      </article>
    </>);

}

export default CourseDetail;